import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";

const EMPTY = { name: "", image: "", category: "atelier", age_min: 4, age_max: 12, date: "", end_date: "",
  time: "", address: "", city: "", region: "", is_free: false, price: "", description: "", lat: "", lng: "",
  official_url: "", territory: "metropole", source: "", child_audio: "", child_audio_en: "", parent_summary: "", active: true };

const TERRITORY_OPTS = [
  { key: "metropole", label: "France métropolitaine" },
  { key: "guadeloupe", label: "Guadeloupe" },
  { key: "martinique", label: "Martinique" },
];

export default function AdminKidsEvents() {
  const [events, setEvents] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/kids-events").then(({ data }) => setEvents(data.events || [])).catch(() => {});
  useEffect(() => { load(); api.get("/kids-events/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const reset = () => { setForm(EMPTY); setEditing(null); };

  const save = async () => {
    if (!form.name.trim() || !form.date) { toast.error("Nom et date obligatoires."); return; }
    const payload = { ...form,
      age_min: Number(form.age_min) || 0, age_max: Number(form.age_max) || 0,
      lat: form.lat === "" ? null : Number(form.lat), lng: form.lng === "" ? null : Number(form.lng) };
    try {
      if (editing) await api.put(`/admin/kids-events/${editing}`, payload);
      else await api.post("/admin/kids-events", payload);
      toast.success(editing ? "Événement mis à jour." : "Événement créé."); reset(); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const edit = (ev) => { setEditing(ev.event_id); setForm({ ...EMPTY, ...ev, lat: ev.lat ?? "", lng: ev.lng ?? "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { if (!window.confirm("Supprimer cet événement ?")) return; try { await api.delete(`/admin/kids-events/${id}`); toast.success("Supprimé."); load(); } catch (e) { toast.error(apiErr(e)); } };

  const inp = "bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm w-full outline-none focus:border-gold/40";

  return (
    <div data-testid="admin-kids-events">
      <div className="card-surface p-4 mb-5 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base text-white">{editing ? "Modifier l'événement" : "Nouvel événement Kids"}</h3>
          {editing && <button onClick={reset} className="text-white/50 text-sm inline-flex items-center gap-1"><X size={14} /> Annuler</button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input className={inp} placeholder="Nom *" value={form.name} onChange={set("name")} data-testid="admin-kev-name" />
          <select className={inp} value={form.category} onChange={set("category")}>{cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select>
          <input className={inp} type="date" value={form.date} onChange={set("date")} data-testid="admin-kev-date" />
          <input className={inp} type="date" placeholder="Fin (option)" value={form.end_date} onChange={set("end_date")} />
          <input className={inp} placeholder="Horaires (ex: 14h-16h)" value={form.time} onChange={set("time")} />
          <input className={inp} placeholder="Ville" value={form.city} onChange={set("city")} />
          <input className={inp} placeholder="Adresse" value={form.address} onChange={set("address")} />
          <input className={inp} placeholder="Région" value={form.region} onChange={set("region")} />
          <input className={inp} type="number" placeholder="Âge min" value={form.age_min} onChange={set("age_min")} />
          <input className={inp} type="number" placeholder="Âge max" value={form.age_max} onChange={set("age_max")} />
          <input className={inp} placeholder="Prix (ex: 8€)" value={form.price} onChange={set("price")} />
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={form.is_free} onChange={set("is_free")} /> Gratuit</label>
          <input className={inp} placeholder="Latitude" value={form.lat} onChange={set("lat")} />
          <input className={inp} placeholder="Longitude" value={form.lng} onChange={set("lng")} />
          <select className={inp} value={form.territory} onChange={set("territory")} data-testid="admin-kev-territory">
            {TERRITORY_OPTS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <input className={inp} placeholder="Source officielle" value={form.source} onChange={set("source")} data-testid="admin-kev-source" />
          <input className={inp + " sm:col-span-2"} placeholder="Image (URL)" value={form.image} onChange={set("image")} />
          <input className={inp + " sm:col-span-2"} placeholder="Lien officiel (infos/réservation)" value={form.official_url} onChange={set("official_url")} />
          <textarea className={inp + " sm:col-span-2"} rows={2} placeholder="Description courte" value={form.description} onChange={set("description")} />
          <textarea className={inp + " sm:col-span-2"} rows={2} placeholder="Audio enfant (français) — présentation orale lue à l'enfant" value={form.child_audio} onChange={set("child_audio")} data-testid="admin-kev-audio-fr" />
          <textarea className={inp + " sm:col-span-2"} rows={2} placeholder="Audio enfant (English) — spoken presentation" value={form.child_audio_en} onChange={set("child_audio_en")} data-testid="admin-kev-audio-en" />
          <textarea className={inp + " sm:col-span-2"} rows={2} placeholder="Résumé pour le parent (infos, sécurité, réservation)" value={form.parent_summary} onChange={set("parent_summary")} data-testid="admin-kev-parent" />
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={form.active} onChange={set("active")} /> Actif (visible)</label>
        </div>
        <button onClick={save} data-testid="admin-kev-save" className="gold-gradient text-black rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><Plus size={15} /> {editing ? "Enregistrer" : "Ajouter"}</button>
      </div>

      <div className="space-y-2" data-testid="admin-kev-list">
        {events.map((ev) => (
          <div key={ev.event_id} className="card-surface p-3 flex items-center gap-3 text-sm">
            <span className="flex-1 min-w-0">
              <span className="text-white truncate block">{ev.name} {!ev.active && <em className="text-white/40">(inactif)</em>}</span>
              <span className="text-white/40 text-xs">{ev.date} · {ev.city} · {ev.category}</span>
            </span>
            <button onClick={() => edit(ev)} className="text-white/60 hover:text-gold"><Pencil size={16} /></button>
            <button onClick={() => del(ev.event_id)} className="text-white/60 hover:text-red-400"><Trash2 size={16} /></button>
          </div>
        ))}
        {events.length === 0 && <p className="text-white/40 text-sm">Aucun événement.</p>}
      </div>
    </div>
  );
}
